import * as TableBasedTemplates from 'types/tableBasedTemplates';
import {
  AdditionalInventorySection,
  AvailableCalendarSquare,
  CalendarSlotsByDateTime,
  CalendarSquareTurnTimeMap,
  CalendarSquareTypes,
  CombinedAvailableCalendarSquare,
  ForfeitedCalendarSquare,
  FullyFlexedTicketGroupInfo,
  HeldCalendarSquare,
  NormalDetailedSquare,
  NormalSquareSubtypes,
  SquaresByTable,
  TableWithCalendarSquares,
} from 'types/tableBasedInventory';
import {
  DEFAULT_MIN_TURN_TIME_MINUTES,
  TABLE_ID_FOR_NO_TABLE,
} from 'constants/TableBasedInventoryConstants';
import { DateTimeOccupancyMap } from 'types/flexibleInventory';
import { InternalSlotDetailsByDateTime, computeSlotDetailsByDateTime } from 'utils/scanLineUtils';
import { LocalDateTime } from 'js-joda';
import {
  NO_TICKET_TYPE_ID_FOR_TURN,
  SQUARE_INCREMENT_MINUTES,
} from 'constants/TableBasedTemplatesConstants';
import { WalkInCalendarParty, dashboard } from 'types';
import { assertNever, filterNilValues } from '@tock/shared/utils/typeUtils';
import { calendarSquaresByTableSelector } from 'selectors/routes/calendar/calendarSquaresByTableSelector';
import { core, safeToNumber } from '@tock/coretypes';
import { createSelector } from 'reselect';
import { dateTimesToRenderSelector } from 'selectors/routes/calendar/flexDateTimesToRenderSelector';
import { fastParseDateTime } from 'utils/timeUtils';
import {
  generateDateTimeOccupancyInfos,
  getBookableIntervalsForRange,
  getFinishedByDateTime,
  getOccupiedTimesForFlexibleEntity,
  getTicketGroupEndTime,
  hasLastBookableTime,
} from 'utils/flexibleTimeUtils';
import { getSectionLabelForTable } from 'utils/tableBasedTemplatesUtils';
import { serviceDayTurnTimesBySizeSelector } from 'selectors/routes/table-based-templates/turnTimesBySizeSelector';
import _ from 'lodash';
import calendarFloorplanSelector, {
  CalendarFloorplan,
} from 'selectors/routes/calendar/calendarFloorplanSelector';

interface TablesWithSlots {
  [tableId: string]: TableWithCalendarSquares;
}

export interface AdditionalInventoryAndTablesWithSlots {
  additionalInventory: AdditionalInventorySection;
  tablesWithSlots: TablesWithSlots;
}

interface TableInfo {
  [tabledId: string]: {
    allSquares: NormalDetailedSquare[];
    table: dashboard.Table;
  };
}

/**
 * For the moment, inactive tables are defined as tables with nothing on them.
 * We choose not to render those on the calendar side.
 */
const SHOULD_SKIP_EMPTY_TABLES = true;

/**
 * This method is responsible for ensuring that every active table in the floorplan
 * is mapped to its relevant squares (even if that's an empty array), as well
 * as ensuring that any archived table in the floorplan that still has inventory
 * or parties assigned to it is rendered.
 *
 * The other role of this function is to stitch together the calendar squares, which
 * are grouped by tableId, with the actual table information like it's size, name, section, and room.
 *
 * One thing to note is that an "active" table that's in an inactive floorplan
 * will appear in the "archived" portion of the floorplan with the isArchived
 * bit artificially set to true by the server.
 */
const keyedTablesSelector = createSelector(
  calendarSquaresByTableSelector,
  calendarFloorplanSelector,
  (
    squaresByTable: SquaresByTable,
    { activeTables, archivedTables }: CalendarFloorplan
  ): TableInfo => {
    const keyedTables: TableInfo = {};
    _.forEach(activeTables, (table) => {
      const tableId = safeToNumber(table.id);

      // Skip the "Additional Inventory" table
      if (tableId === TABLE_ID_FOR_NO_TABLE) {
        return;
      }
      const calendarSquares: NormalDetailedSquare[] = squaresByTable[tableId] || [];

      keyedTables[tableId] = {
        allSquares: calendarSquares,
        table,
      };
    });

    _.forEach(archivedTables, (table) => {
      const tableId = safeToNumber(table.id);
      const calendarSquares: NormalDetailedSquare[] = squaresByTable[tableId];
      if (_.isEmpty(calendarSquares)) {
        return;
      }
      keyedTables[tableId] = {
        allSquares: calendarSquares,
        table,
      };
    });
    return keyedTables;
  }
);

/**
 * Reads the squares with no table (keyed by TABLE_ID_FOR_NO_TABLE)
 * and passes them to constructAdditionalInventory, which does some minimal
 * data massaging.
 */
const additionalInventorySelector = createSelector(
  calendarSquaresByTableSelector,
  (squaresByTable: SquaresByTable): AdditionalInventorySection => {
    return constructAdditionalInventory(squaresByTable[TABLE_ID_FOR_NO_TABLE] || []);
  }
);

function computeCalendarSquaresByTableAndTime(
  keyedTables: TableInfo,
  dateTimesToRender: string[],
  additionalInventory: AdditionalInventorySection,
  turnTimes: TableBasedTemplates.TurnTimes
): AdditionalInventoryAndTablesWithSlots {
  const tablesWithSlotsOrUndefined = _.mapValues(keyedTables, (tableInfo, tableId) => {
    return toTablesWithSlots(tableInfo, tableId, turnTimes, dateTimesToRender);
  });
  const tablesWithSlots = _.pickBy(tablesWithSlotsOrUndefined, filterNilValues);

  return {
    additionalInventory,
    tablesWithSlots,
  };
}

/**
 * The output of this selector is all the data necessary to render the expanded (detailed) view
 * of the flex calendar body. Most of the heavy lifting is done by toTablesWithSlots.
 * For an overview of the data flow, see the below Google Doc
 * https://docs.google.com/document/d/1S7ZhALbdjnKxq7mgo4I4Ogk50oZUCNekl09S2NRud2w/edit
 */
export default createSelector(
  keyedTablesSelector,
  dateTimesToRenderSelector,
  additionalInventorySelector,
  serviceDayTurnTimesBySizeSelector,
  computeCalendarSquaresByTableAndTime
);

/**
 * From a list of calendar squares, go into the ticket groups and pull out the
 * tables with auto release enabled.
 *
 * This gets called for every table, usually with the same underlying set of
 * ticket groups, so there's some memoization here to avoid re-computing this
 * set every time.
 */
const getTableIdsWithAutoRelease = (() => {
  const getTableIds = (squares: NormalDetailedSquare[]) =>
    new Set(_.flatMap(squares, (square) => square.ticketGroup?.tableAutoReleasedId));

  // In theory, sorting the ticket groups when forming the cache key will lead
  // to more cache hits. In practice, the ticket groups will usually come in the
  // same order, so sorting the ticket groups every time incurs more of a cost.
  const getCacheKey = (squares: NormalDetailedSquare[]) =>
    squares.map((square) => square.ticketGroup?.id).join('-');

  return _.memoize(getTableIds, getCacheKey);
})();

/**
 * This method is responsible for massaging all of the calendar squares for the provided table into the correct
 * shape(s) for rendering in the detailed view of the calendar body. The proceeds in the following steps:
 *
 * 1. We filter out available squares that are not truly available. We do this by building a map
 *    of all the times the table is occupied or otherwise can't fit a turn, and then filtering out
 *    available squares if they are not at a "truly available" time. This is necessary b/c when we
 *    first generate calendar items / calendar squares, we don't have access to turn time information
 *    and so blindly create availability every 15 minutes.
 * 2. We combine contiguous available squares into blocks of availability.
 * 3. Organize all of the squares by date time
 * 4. Figure out if the table has conflicts, and at what start times. We use this information to render a red background.
 *    It's also by this process that we figure out how "tall" the row needs to be, which is then used to construct
 *    a css style object with the appropriate height.
 * 5. Put it all together under the "slots" key of the table
 *
 * More information can be found in the below google doc:
 * https://docs.google.com/document/d/1S7ZhALbdjnKxq7mgo4I4Ogk50oZUCNekl09S2NRud2w/edit
 *
 * @param tableInfo This is the table details and calendar squares of the table we're working with
 * @param tableId This is the id of the table we're looking at
 * @param turnTimes This is an object with all the turn times specified by the business
 * @param dateTimesToRender The list of all dateTimes that we're going to show in the header of the calendar body
 * @returns The details of the provided table along with the calendar squares we should render, organized by date time.
 * It can also return undefined if the table has no assigned parties or inventory, which currently represents
 * a table being "inactive".
 */
function toTablesWithSlots(
  tableInfo: {
    allSquares: NormalDetailedSquare[];
    table: dashboard.Table;
  },
  tableId: string,
  turnTimes: TableBasedTemplates.TurnTimes,
  dateTimesToRender: string[]
): TableWithCalendarSquares | undefined {
  const table = tableInfo.table;
  const tableIdNumber = safeToNumber(table.id);
  const calendarSquares = tableInfo.allSquares;
  const seatingArea = table.seatingArea ?? undefined;
  const tableIdsWithAutoRelease = getTableIdsWithAutoRelease(calendarSquares);

  if (_.isEmpty(calendarSquares)) {
    if (SHOULD_SKIP_EMPTY_TABLES) {
      return undefined;
    }
    return {
      isAutoReleased: tableIdsWithAutoRelease.has(tableIdNumber),
      height: 1,
      id: tableId,
      tableId: _.toNumber(tableId),
      isArchived: table.isArchived,
      minSize: table.minPartySize,
      name: table.name,
      room: table.room,
      seatingArea: seatingArea,
      section: table.section,
      sectionLabel: getSectionLabelForTable({ room: table.room, section: table.section }),
      size: table.size,
      slots: {},
      type: 'tableWithCalendarSquares',
    };
  }
  const purchasesAndSubsets: {
    purchases: core.PastPurchase[];
    heldSubsets: core.TicketSubset[];
    lockedSubsets: core.TicketSubset[];
    walkIns: WalkInCalendarParty[];
  } = gatherPurchasesAndSubsets(calendarSquares);

  const occupancyMap = getOccupancyMapForTable(calendarSquares, turnTimes, purchasesAndSubsets);
  const heldFlexSquares: HeldCalendarSquare[] = [];
  const otherSquares: NormalDetailedSquare[] = [];
  const availableFlexSquares: AvailableCalendarSquare[] = [];

  const forfeitedSquares: ForfeitedCalendarSquare[] = [];
  _.forEach(calendarSquares, (calendarSquare: NormalDetailedSquare) => {
    const occupancyAtTime = occupancyMap[calendarSquare.startDateTimeString];
    if (calendarSquare.subType === 'AVAILABLE' && tableIdNumber === calendarSquare.tableId) {
      availableFlexSquares.push(calendarSquare);
    }
    if (
      calendarSquare.subType === 'AVAILABLE' &&
      calendarSquare.ticketGroup.finishedByTime &&
      occupancyAtTime &&
      occupancyAtTime.occupiedState.type !== 'AVAILABLE'
    ) {
      forfeitedSquares.push({
        anticipatedEndDateTime: calendarSquare.startDateTime.plusMinutes(SQUARE_INCREMENT_MINUTES),
        calendarItem: calendarSquare.calendarItem,
        id: calendarSquare.calendarItem.id,
        startDateTime: calendarSquare.startDateTime,
        startDateTimeString: calendarSquare.startDateTimeString,
      });
      return;
    }
    if (calendarSquare.subType === 'HELD' && calendarSquare.isOnFlexAvailability) {
      heldFlexSquares.push(calendarSquare);
    } else {
      otherSquares.push(calendarSquare);
    }
  });

  const calendarSquaresWithAvailableCombined: NormalDetailedSquare[] =
    combineAvailable(otherSquares);

  const { maxRow, renderDetailsByDateTime } = computeSlotDetailsByDateTime(
    calendarSquaresWithAvailableCombined
  );

  const heldFlexSquaresByDateTime = _.keyBy(
    heldFlexSquares,
    (square) => square.startDateTimeString
  );
  const availableFlexSquaresByDateTime = _.groupBy(
    availableFlexSquares,
    (square) => square.startDateTimeString
  );
  const forfeitedSquaresByDateTime = _.keyBy(
    forfeitedSquares,
    (forfeitedSquare: ForfeitedCalendarSquare) => forfeitedSquare.startDateTimeString
  );
  const dateTimes = _.uniq(
    _.concat(
      _.map(dateTimesToRender, _.toString),
      _.keys(renderDetailsByDateTime),
      _.keys(heldFlexSquaresByDateTime),
      _.keys(forfeitedSquaresByDateTime)
    )
  );

  const renderDetailsWithConflictsByDateTime = detectConflicts(
    dateTimes,
    renderDetailsByDateTime,
    availableFlexSquaresByDateTime
  );

  return {
    isAutoReleased: tableIdsWithAutoRelease.has(tableIdNumber),
    fullyFlexedTicketGroupInfo: getFullyFlexedTicketGroupInfo(
      calendarSquares,
      purchasesAndSubsets.heldSubsets
    ),
    seatingArea: seatingArea,
    height: maxRow < 1 ? 1 : maxRow,
    id: tableId,
    tableId: _.toNumber(tableId),
    isArchived: table.isArchived,
    name: table.name,
    minSize: table.minPartySize,
    room: table.room,
    section: table.section,
    sectionLabel: getSectionLabelForTable({ room: table.room, section: table.section }),
    size: table.size,
    slots: _.reduce(
      dateTimes,
      (slots: CalendarSlotsByDateTime, dateTime: string) => {
        const detailsAtTime = renderDetailsWithConflictsByDateTime[dateTime];
        const calendarSquareIds: string[] = [];
        const calendarSquaresById: Dictionary<NormalDetailedSquare> = {};
        if (detailsAtTime) {
          _.forEach(detailsAtTime.calendarSquares, (square: NormalDetailedSquare) => {
            calendarSquareIds.push(square.id);
            calendarSquaresById[square.id] = square;
          });
        }
        slots[dateTime] = {
          calendarSquareIds,
          calendarSquaresById,
          calendarSquares: detailsAtTime ? detailsAtTime.calendarSquares : [],
          inConflict: detailsAtTime ? detailsAtTime.inConflict : false,
          isOccupied: detailsAtTime ? detailsAtTime.isOccupied : false,
          forfeitedSquare: forfeitedSquaresByDateTime[dateTime],
          heldFlexSquare: heldFlexSquaresByDateTime[dateTime],
          availableSquares: availableFlexSquaresByDateTime[dateTime],
        };
        return slots;
      },
      {}
    ),
    type: 'tableWithCalendarSquares',
  };
}

/**
 * This method determines which date times are occupied and which are part of a conflict.
 * It starts by looping over all the times we're going to render. If there are any time
 * blocks that haven't ended yet (`numNotYetEnded`) and at least one of those counts
 * as occupied (e.g. is a party), then we mark the square as occupied.
 *
 * We also track if we're in a conflict by reading from the render details at the start/end
 * of a conflict. That information is determined by `computeSlotDetailsByDateTimeFromTimeItems` in `scanLineUtils`.
 * When we see a time being a start of a conflict, we mark that we're in a conflict and put that information
 * on every dateTime until we get to a square that's the end of the conflict.
 */
function detectConflicts(
  dateTimesStringsToRender: string[],
  renderDetailsByDateTime: InternalSlotDetailsByDateTime<NormalDetailedSquare>,
  availableFlexSquaresByDateTime: Dictionary<AvailableCalendarSquare[]>
) {
  const result: CalendarSlotsByDateTime = {};
  let inConflict = false;
  let isInOccupiedBlock = false;
  _.forEach(dateTimesStringsToRender, (dateTimeString) => {
    const renderDetails = renderDetailsByDateTime[dateTimeString] || {};
    const calendarSquares = renderDetails.items || [];
    const calendarSquareIds: string[] = [];
    const calendarSquaresById: Dictionary<NormalDetailedSquare> = {};
    let isOccupied = false;
    _.forEach(calendarSquares, (square: NormalDetailedSquare) => {
      calendarSquareIds.push(square.id);
      calendarSquaresById[square.id] = square;
    });
    if (_.isNumber(renderDetails.numNotYetEnded)) {
      isOccupied =
        renderDetails.numNotYetEnded > 0 && _.some(calendarSquares, isCalendarSquareOccupied);
    }
    const isTimeInConflict = !!(inConflict || renderDetails.startConflict);

    result[dateTimeString] = {
      inConflict: isTimeInConflict,
      isOccupied: isInOccupiedBlock ? true : isOccupied,
      calendarSquares,
      calendarSquareIds,
      calendarSquaresById,
      availableSquares: availableFlexSquaresByDateTime[dateTimeString],
    };

    if (_.isNumber(renderDetails.numNotYetEnded)) {
      isInOccupiedBlock =
        renderDetails.numNotYetEnded > 0 && _.some(calendarSquares, isCalendarSquareOccupied);
    }

    // The next step of the loop is still in a conflict unless this time is the end of a conflict
    inConflict = isTimeInConflict && !renderDetails.endConflict;
  });
  return result;
}

/** Small utitilty function for mapping a calendar square's type to wether it counts as occupied. */
function isCalendarSquareOccupied(calendarSquare: NormalDetailedSquare) {
  switch (calendarSquare.subType) {
    case 'AVAILABLE':
    case 'HELD':
      return false;
    case 'LOCKED':
    case 'BOOKED':
    case 'WALK_IN':
      return true;
    default:
      assertNever(calendarSquare);
      return true;
  }
}

/**
 * If all of the ticket groups assigned to a table are "time flexible", we want the
 * table to be selectable directly so that blocks or holds can be applied. This method
 * determines whether all of the assigned ticket groups are time flexible and if so,
 * returns the hold and block details about those ticket groups.
 *
 * At one point we planned to support "slots" on tables and this method allowed
 * us to prevent those tables from being selected directly. Slots on tables is no longer
 * a use case that we support.
 */
function getFullyFlexedTicketGroupInfo(
  calendarSquares: NormalDetailedSquare[],
  heldSubsets: core.TicketSubset[]
): FullyFlexedTicketGroupInfo | undefined {
  // TODO(jacob, 2019-02-05): This function should work on the ticket groups
  // directly. It seems weird that it's working on squares (a rendering concept)
  // and working backwards to discover the underlying ticket groups.
  const openInventoryWithDups = _.flatMap(calendarSquares, (square) => {
    if (
      square.type === CalendarSquareTypes.NORMAL &&
      (square.subType === NormalSquareSubtypes.AVAILABLE ||
        square.subType === NormalSquareSubtypes.HELD)
    ) {
      return [square.ticketGroup];
    } else {
      return [];
    }
  });
  const openInventory = _.uniqBy(openInventoryWithDups, (item) => item.id);
  const allAvailabilityIsFlex = _.every(openInventory, (ticketGroup) => ticketGroup.finishedByTime);
  if (allAvailabilityIsFlex) {
    return {
      heldSubsetIds: _.map(heldSubsets, (subset) => subset.id),
      seatingBlocks: _.map(openInventory, (ticketGroup) => {
        const startDateTime = fastParseDateTime(ticketGroup.dateTime);

        // When the last time is bookable extend 15 minutes to include as bookable
        const ticketGroupEndTime = getTicketGroupEndTime(ticketGroup);
        const finishedByDateTime = hasLastBookableTime(ticketGroup)
          ? getFinishedByDateTime(startDateTime, ticketGroupEndTime).plusMinutes(
              SQUARE_INCREMENT_MINUTES
            )
          : getFinishedByDateTime(startDateTime, ticketGroupEndTime);

        return {
          ticketGroupId: ticketGroup.id,
          finishedByDateTime,
          startDateTime,
          ticketTypeId: ticketGroup.ticketType[0].id as number,
        };
      }),
    };
  } else {
    return undefined;
  }
}

/**
 * This method takes all the calendar squares and replaces contiguous available
 * squares with a single square of availability with the calendar items of the
 * contiguous squares as a sub-key.
 *
 * I (YK 2019/06/04) am not 100% sure why I did this in the first place. It may have been done at
 * a time when we still thought we would allow time flexible ticket groups
 * in additional inventory. For sure it evolved from code that supported that.
 * At it's core, the goal of this was to have an abstraction that allowed us to
 * support time-flexible availability blocks as well as "slotted" availability as the same thing.
 */
function combineAvailable(calendarSquares: NormalDetailedSquare[]): NormalDetailedSquare[] {
  const squaresWithAvailableCombined: NormalDetailedSquare[] = [];
  let availableBlock: AvailableCalendarSquare[] = [];
  // This takes a list of contiguous available squares and turns them into a single
  // availability square with the calendar item's of the
  // contiguous squares as a sub-key under `availableCalendarItems`.
  // It sets the turnTime and anticipatedEndDateTime to be that of the last square, so that
  // we can correctly draw the tail of the availability block.
  const constructCombinedAvailableSquare = (
    availableSquares: AvailableCalendarSquare[]
  ): CombinedAvailableCalendarSquare => {
    const firstSquare = availableSquares[0];
    const lastSquare = availableSquares[availableSquares.length - 1];
    // All the metadata should be the same for all the available squares
    return {
      ...firstSquare,
      isCombined: true,
      availableCalendarItems: _.flatMap(
        availableSquares,
        (availableSquare) => availableSquare.availableCalendarItems
      ),
      anticipatedEndDateTime: lastSquare.anticipatedEndDateTime,
      // @todo (linnea) - should we deprecate this? figuring out this value was the last turn time in a combined range
      // was a bit of a rabbit hole. with turns able to be configured by range this data may be more confusing than useful
      turnTime: lastSquare.turnTime,
      turnTimesByTime: availableSquares.reduce<CalendarSquareTurnTimeMap>(
        (acc, square) => ({
          ...acc,
          ...(square.turnTime ? { [square.startDateTime.toString()]: square.turnTime } : {}),
        }),
        {}
      ),
    };
  };
  _.forEach(calendarSquares, (calendarSquare) => {
    if (calendarSquare.subType === 'AVAILABLE') {
      // Only create a combined block for available inventory in the same ticket group
      // This is purely to be defensive against bad data (YK 2018/12/21)
      const isAllSameTicketGroup = _.every(
        availableBlock,
        (availableSquare: AvailableCalendarSquare) => {
          return availableSquare.ticketGroup.id === calendarSquare.ticketGroup.id;
        }
      );
      if (isAllSameTicketGroup) {
        availableBlock.push(calendarSquare);
      } else {
        if (!_.isEmpty(availableBlock)) {
          squaresWithAvailableCombined.push(constructCombinedAvailableSquare(availableBlock));
        }
        availableBlock = [];
        availableBlock.push(calendarSquare);
      }
    } else {
      if (!_.isEmpty(availableBlock)) {
        squaresWithAvailableCombined.push(constructCombinedAvailableSquare(availableBlock));
      }
      squaresWithAvailableCombined.push(calendarSquare);
      availableBlock = [];
    }
  });
  if (!_.isEmpty(availableBlock)) {
    squaresWithAvailableCombined.push(constructCombinedAvailableSquare(availableBlock));
  }
  return squaresWithAvailableCombined;
}

export interface PurchasesAndSubsets {
  heldSubsets: core.TicketSubset[];
  lockedSubsets: core.TicketSubset[];
  purchases: core.PastPurchase[];
  walkIns: WalkInCalendarParty[];
}

/**
 * Given a list of calendar squares, collects and organizes all of the
 * held and locked subsets, as well as purchases and walk-ins.
 */
export function gatherPurchasesAndSubsets(
  calendarSquares: NormalDetailedSquare[]
): PurchasesAndSubsets {
  const heldSubsets: core.TicketSubset[] = [];
  const lockedSubsets: core.TicketSubset[] = [];
  const purchases: core.PastPurchase[] = [];
  const walkIns: WalkInCalendarParty[] = [];
  _.forEach(calendarSquares, (calendarSquare) => {
    switch (calendarSquare.subType) {
      case 'HELD': {
        heldSubsets.push(calendarSquare.ticketSubset);
        break;
      }
      case 'LOCKED': {
        lockedSubsets.push(calendarSquare.ticketSubset);
        break;
      }
      case 'BOOKED': {
        purchases.push(calendarSquare.purchase);
        break;
      }
      case 'WALK_IN': {
        walkIns.push(calendarSquare.party);
        break;
      }
      case 'AVAILABLE':
        break;
      default: {
        assertNever(calendarSquare);
      }
    }
  });
  return { purchases, heldSubsets, lockedSubsets, walkIns };
}

/**
 * Creates a mapping from dateTime to whether that time is occupied or available
 * This is used primarily (entirely?) to filter out "Available" squares
 * that are not actually avaliable.
 *
 * We generate an "Available" square for every 15 minutes in the bookable range,
 * basically because it was convenient at one point in calendarItems, which aren't generated
 * inside a selector and so didn't have access to things like turn time information.
 *
 * Note that only squares with the same serviceType can interfere with each other. This is
 * so that if there are overlapping lunch and dinner ticket groups we can render those separately
 * instead of treating them all as one large available block.
 * However, that is not a supported way of configuring inventory (stuff is still weird),
 * so maybe it would be better to take a different approach.
 */
export function getOccupancyMapForTable(
  allCalendarSquares: NormalDetailedSquare[],
  turnTimes: TableBasedTemplates.TurnTimes,
  { purchases, lockedSubsets, walkIns }: PurchasesAndSubsets
): DateTimeOccupancyMap {
  const squaresByServiceType = _.groupBy(
    allCalendarSquares,
    (calendarSquare: NormalDetailedSquare) => {
      return calendarSquare.serviceType;
    }
  );
  return _.reduce<NormalDetailedSquare[], DateTimeOccupancyMap>(
    _.values(squaresByServiceType),
    (acc, calendarSquares) => {
      const firstSquare = _.first(calendarSquares);
      const lastSquare = _.last(calendarSquares);

      if (!firstSquare || !lastSquare) {
        return acc;
      }

      // The anticipatedEndDateTime of the last square can either represent the last booking time or the end of service
      // When it's the last booking time we make sure to add the turn time to the square to account for the tail
      const lastBookableTimeEnabled =
        lastSquare.calendarItem.ticketGroup &&
        hasLastBookableTime(lastSquare.calendarItem.ticketGroup);
      const finishedByDateTime = finishedByDateTimeFromSquares(
        calendarSquares,
        lastBookableTimeEnabled
      );

      const bookableRangeForTable: LocalDateTime[] = getBookableIntervalsForRange(
        {
          startDateTime: firstSquare.startDateTime,
          finishedByDateTime,
        },
        lastBookableTimeEnabled
      );

      const occupiedTimes = getOccupiedTimesForFlexibleEntity(
        { purchases, ticketSubsets: lockedSubsets, walkIns },
        turnTimes,
        NO_TICKET_TYPE_ID_FOR_TURN
      );
      const squaresByStartTime = _.groupBy(
        calendarSquares,
        (calendarSquare: NormalDetailedSquare) =>
          calendarSquare.startDateTime.toLocalTime().toString()
      );
      // One way that a start time can be unavailable is if there is not enough
      // time before the end of the availability window to fit a full turn.
      // With the introduction of varying turn times by start time, the minimum turn time
      // can vary by start time so we construct an object mapping start times to the min turn time
      const minTurnTimeMinutesByStartTime = _.mapValues(squaresByStartTime, (calendarSquares) => {
        const min = _.min<number>(
          _.map(calendarSquares, (calendarSquare) => {
            if (calendarSquare.subType !== 'AVAILABLE' || !calendarSquare.turnTime) {
              return Infinity;
            }
            return calendarSquare.turnTime.turnTimeMinutes;
          })
        );
        return min || DEFAULT_MIN_TURN_TIME_MINUTES;
      });
      generateDateTimeOccupancyInfos(
        bookableRangeForTable,
        occupiedTimes,
        minTurnTimeMinutesByStartTime
      ).forEach((occupancy) => {
        acc[occupancy.localDateTime.toString()] = occupancy;
      });
      return acc;
    },
    {}
  );
}

/**
 * Gets the last possible time at which all parties must leave from a group of
 * calendar squares.
 *
 * When the service's end time defines the time at which the last party must
 * leave, just return the anticipatedEndDateTime of the last square. (This is a
 * deprecated mode that we're phasing out, but we still need this to work for
 * past inventory.)
 *
 * When the service can have bookings starting until the end time, we add the
 * turn time to each square and compute the max datetime from that. This will
 * _usually_ return the end time of the last square plus its turn time, but may
 * return something else if the business has reduced its turn times near the
 * end of the service.
 *
 * @param calendarSquares An ordered, non-empty array of calendar squares
 * @param lastBookableTimeEnabled Whether this service can be booked until the
 * service's end time
 * @returns The last possible time at which all parties must leave.
 */
export function finishedByDateTimeFromSquares(
  calendarSquares: Pick<NormalDetailedSquare, 'anticipatedEndDateTime' | 'turnTime'>[],
  lastBookableTimeEnabled: boolean
): LocalDateTime {
  const lastSquare = _.last(calendarSquares);
  if (!lastSquare) {
    // This only throws if calendarSquares is empty.
    throw new Error('Cannot compute finishedByDateTime from empty calendarSquares array.');
  }
  if (!lastBookableTimeEnabled) {
    return lastSquare.anticipatedEndDateTime;
  }

  const finishedByDateTime = _.max(
    calendarSquares.map((square) =>
      square.anticipatedEndDateTime.plusMinutes(square.turnTime?.turnTimeMinutes ?? 0).toString()
    )
  );
  // istanbul ignore next
  if (!finishedByDateTime) {
    // This only throws if calendarSquares is empty.
    throw new Error('Cannot compute finishedByDateTime from empty calendarSquares array.');
  }
  return LocalDateTime.parse(finishedByDateTime);
}

/**
 * This method takes a list of calendarSquares that are in additional inventory,
 * meaning they are not assigned to any table, and makes a couple small changes.
 * 1. It sets the tableId of those squares to `undefined`. When the
 * groupBy is run in the previous step the tableId is set to TABLE_ID_FOR_NO_TABLE (or -1),
 * so that the object can have keys of all the same type.
 * 2. It figures out how tall the additional inventory section needs to be so that
 * it can be given the correct height
 * 3. It removes any disputed and cancelled items from being returned.
 *
 * Note that in the UI, the header for additional inventory is now called "Unassigned Reservations"
 */
export function constructAdditionalInventory(
  squares: NormalDetailedSquare[]
): AdditionalInventorySection {
  const squaresWithoutTables = squares
    .filter(
      (square) =>
        // This avoids showing 0 slots for normalized inventory, while still displaying purchases
        !square.ticketGroup || square.ticketGroup.maxSize > 0 || square.calendarItem.purchase
    )
    .map((calendarSquare) => {
      return {
        ...calendarSquare,
        tableId: undefined,
      };
    });

  const { maxRow, renderDetailsByDateTime } = computeSlotDetailsByDateTime(squaresWithoutTables);
  const additionalInventorySquares = !_.isEmpty(squaresWithoutTables)
    ? _.mapValues(renderDetailsByDateTime, (i) => i.items)
    : {};
  return {
    calendarSquares: additionalInventorySquares,
    sectionHeight: maxRow,
  };
}
